import React, { useState } from 'react';
import './App.css';
import Header from './components/Header';
import Hero from './components/Hero';
import SocialProof from './components/SocialProof';
import CorporateSLA from './components/CorporateSLA';
import B2BLeadForm from './components/B2BLeadForm';
import AppDownloadSection from './components/AppDownloadSection';
import HowItWorks from './components/HowItWorks';
import RegisterSection from './components/RegisterSection';
import Footer from './components/Footer';
import PrivacyPolicy from './components/PrivacyPolicy';
import TermsOfUse from './components/TermsOfUse';
import CookiesPolicy from './components/CookiesPolicy';
import HelpSupport from './components/HelpSupport';
import CookieBanner from './components/CookieBanner';
import AboutUs from './components/AboutUs';
import SEOManager from './components/SEOManager';
import CoverageAreas from './components/CoverageAreas';

export type ViewMode = 'pessoal' | 'empresa' | 'privacy' | 'terms' | 'cookies' | 'help' | 'about';

function App() {
  const [lastHomeMode, setLastHomeMode] = useState<'pessoal' | 'empresa'>(() => {
    const saved = localStorage.getItem('lastHomeMode');
    return (saved === 'pessoal' || saved === 'empresa') ? saved : 'empresa';
  });
  const [viewMode, setViewMode] = useState<ViewMode>(() => {
    const hash = window.location.hash.replace('#', '');
    if (['pessoal', 'empresa', 'privacy', 'terms', 'cookies', 'help', 'about'].includes(hash)) {
      return hash as ViewMode;
    }
    const saved = localStorage.getItem('lastHomeMode');
    return (saved === 'pessoal' || saved === 'empresa') ? saved : 'empresa';
  });

  // Track the last active home view mode ('pessoal' or 'empresa')
  React.useEffect(() => {
    if (viewMode === 'pessoal' || viewMode === 'empresa') {
      setLastHomeMode(viewMode);
      localStorage.setItem('lastHomeMode', viewMode);
    }
  }, [viewMode]);

  // Handle Hash Routing
  React.useEffect(() => {
    const handleHashChange = () => {
      const hash = window.location.hash.replace('#', '');
      if (['pessoal', 'empresa', 'privacy', 'terms', 'cookies', 'help', 'about'].includes(hash)) {
        setViewMode(hash as ViewMode);
        window.scrollTo(0, 0);
      }
    };
    
    // Check hash on mount
    handleHashChange();

    window.addEventListener('hashchange', handleHashChange);
    return () => window.removeEventListener('hashchange', handleHashChange);
  }, []);

  // Smooth mouse movement parallax with inertia
  React.useEffect(() => {
    let requestRef = 0;
    let mouseX = 0;
    let mouseY = 0;
    let currentX = 0;
    let currentY = 0;

    const handleMouseMove = (e: MouseEvent) => {
      // Normalize position relative to center of viewport
      mouseX = (e.clientX - window.innerWidth / 2) / 25;
      mouseY = (e.clientY - window.innerHeight / 2) / 25;
    };

    const animate = () => {
      // Smooth linear interpolation (inertia)
      currentX += (mouseX - currentX) * 0.08;
      currentY += (mouseY - currentY) * 0.08;

      document.documentElement.style.setProperty('--mouse-x', `${currentX}px`);
      document.documentElement.style.setProperty('--mouse-y', `${currentY}px`);

      requestRef = requestAnimationFrame(animate);
    };

    window.addEventListener('mousemove', handleMouseMove);
    requestRef = requestAnimationFrame(animate);

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      cancelAnimationFrame(requestRef);
    };
  }, []);

  const handleSetViewMode = (mode: ViewMode) => {
    setViewMode(mode);
    window.location.hash = mode;
    window.scrollTo(0, 0);
  };

  const renderContent = () => {
    switch (viewMode) {
      case 'privacy':
        return <PrivacyPolicy />;
      case 'terms':
        return <TermsOfUse />;
      case 'cookies':
        return <CookiesPolicy />;
      case 'help':
        return <HelpSupport />;
      case 'about':
        return <AboutUs />;
      case 'empresa':
        return (
          <div className="animate-fadeIn">
            <Hero mode={viewMode} />
            <SocialProof mode={viewMode} />
            <CorporateSLA />
            <CoverageAreas />
            <B2BLeadForm />
          </div>
        );
      case 'pessoal':
      default:
        return (
          <div className="animate-fadeIn">
            <Hero mode={viewMode} />
            <SocialProof mode={viewMode} />
            <AppDownloadSection />
            <HowItWorks />
            <CoverageAreas />
            <RegisterSection setViewMode={handleSetViewMode} />
          </div>
        );
    }
  };

  return (
    <div className="App font-sans bg-white min-h-screen flex flex-col relative overflow-hidden">
      <SEOManager viewMode={viewMode} />
      {/* Dynamic Sea Floating Background */}
      <div className="sea-floating-bg">
        <div className="sea-blob sea-blob-1"></div>
        <div className="sea-blob sea-blob-2"></div>
        <div className="sea-blob sea-blob-3"></div>
      </div>

      <Header viewMode={viewMode} setViewMode={handleSetViewMode} lastHomeMode={lastHomeMode} />
      <main className="flex-grow relative z-10">
        {renderContent()}
      </main>
      <Footer viewMode={viewMode} setViewMode={handleSetViewMode} />
      <CookieBanner />
    </div>
  );
}

export default App;
